import { useState, useReducer } from 'react';
import { useApi } from '../hooks/useApi';

// ── Inline primitives ─────────────────────────────────────────────────────────

const PAGE = {
  display: 'flex', flexDirection: 'column', height: '100%',
  background: 'var(--bg)', color: 'var(--text)', fontFamily: 'inherit',
};
const HEADER = {
  padding: '20px 24px 0',
  borderBottom: '1px solid var(--border)',
};
const H1 = {
  margin: '0 0 4px', fontSize: 22, fontWeight: 700, color: 'var(--text)',
};
const SUB = {
  margin: '0 0 16px', fontSize: 13, color: 'var(--text-muted)',
};
const BODY = {
  flex: 1, overflowY: 'auto', padding: 24,
};
const GRID = (cols = 4) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${cols}, 1fr)`,
  gap: 16,
  marginBottom: 24,
});
const CARD = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '16px 20px',
  boxShadow: 'var(--shadow-lg)',
};
const TABLE_WRAP = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  overflow: 'hidden',
  marginBottom: 24,
};
const TABLE = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13,
};
const TH = {
  background: 'var(--bg)', padding: '10px 14px',
  textAlign: 'left', fontWeight: 600, fontSize: 12,
  color: 'var(--text-muted)', borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap',
};
const TD = {
  padding: '10px 14px', borderBottom: '1px solid var(--border)',
  verticalAlign: 'middle',
};

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function SectionTitle({ children, style }) {
  return (
    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 12, ...style }}>
      {children}
    </div>
  );
}

function StatCard({ label, value, sub, accent }) {
  const border = accent ? `2px solid ${accent}` : '1px solid var(--border)';
  return (
    <div style={{ ...CARD, border }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted)', marginBottom: 6 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: accent ?? 'var(--text)', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      {sub && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
      )}
    </div>
  );
}

function Badge({ text, color = '#888' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, letterSpacing: '0.03em',
      background: color + '22', color,
    }}>
      {text}
    </span>
  );
}

function SelectEl({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={onChange} style={{
      padding: '6px 10px', border: '1px solid var(--border)',
      borderRadius: 'var(--radius)', background: 'var(--surface)',
      color: 'var(--text)', fontSize: 13, ...style,
    }}>
      {children}
    </select>
  );
}

function EmptyRow({ cols, msg = 'No records found' }) {
  return (
    <tr>
      <td colSpan={cols} style={{ ...TD, textAlign: 'center', color: 'var(--text-muted)', padding: '32px 14px' }}>
        {msg}
      </td>
    </tr>
  );
}

function LoadingRow({ cols }) {
  return (
    <tr>
      <td colSpan={cols} style={{ ...TD, textAlign: 'center', color: 'var(--text-muted)', padding: '32px 14px' }}>
        Loading…
      </td>
    </tr>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'overview',   label: '📊 Overview' },
  { id: 'production', label: '🏭 Production' },
  { id: 'stock',      label: '📦 Stock' },
  { id: 'hmrc',       label: '🏛 HMRC / Duty' },
  { id: 'salsa',      label: '✅ SALSA' },
];

function TabBar({ tab, setTab }) {
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {TABS.map(t => (
        <button
          key={t.id}
          onClick={() => setTab(t.id)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 18px', fontSize: 13, fontWeight: 600,
            color: tab === t.id ? 'var(--pld-green)' : 'var(--text-muted)',
            borderBottom: tab === t.id ? '2px solid var(--pld-green)' : '2px solid transparent',
            marginBottom: -1, transition: 'all 0.15s',
          }}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function runStatusColor(s) {
  return { planned: '#6b7280', in_progress: '#d97706', completed: '#16a34a', cancelled: '#dc2626' }[s] ?? '#6b7280';
}
function runStatusLabel(s) {
  return { planned: 'Planned', in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled' }[s] ?? s;
}
function w1StatusColor(s) {
  return { open: '#2563eb', locked: '#d97706', submitted: '#7c3aed', acknowledged: '#16a34a' }[s] ?? '#6b7280';
}
function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ v }) {
  const { data: runsRaw, loading: runsLoading } = useApi('/production/runs?limit=200', [v]);
  const { data: alertsRaw } = useApi('/stock/alerts/unresolved', [v]);
  const { data: w1Raw }     = useApi('/hmrc/w1-periods', [v]);
  const { data: salsa }     = useApi('/salsa/dashboard', [v]);

  const runs   = runsRaw?.runs ?? [];
  const alerts = alertsRaw?.alerts ?? [];
  const w1     = Array.isArray(w1Raw) ? w1Raw : [];

  const inProgress = runs.filter(r => r.status === 'in_progress').length;
  const planned    = runs.filter(r => r.status === 'planned').length;
  const completed  = runs.filter(r => r.status === 'completed').length;

  const openW1   = w1.find(p => p.status === 'open');
  const lockedW1 = w1.find(p => p.status === 'locked');
  const latestW1 = w1[0];

  return (
    <div>
      <SectionTitle>Production</SectionTitle>
      <div style={GRID(4)}>
        <StatCard label="In Progress" value={runsLoading ? '…' : inProgress} accent={inProgress > 0 ? '#d97706' : undefined} />
        <StatCard label="Planned" value={runsLoading ? '…' : planned} />
        <StatCard label="Completed (all time)" value={runsLoading ? '…' : completed} accent={completed > 0 ? '#16a34a' : undefined} />
        <StatCard label="Total Runs" value={runsLoading ? '…' : runs.length} />
      </div>

      <SectionTitle>Stock</SectionTitle>
      <div style={GRID(2)}>
        <StatCard
          label="Unresolved Alerts"
          value={alerts.length}
          accent={alerts.length > 0 ? '#dc2626' : undefined}
          sub={alerts.length > 0 ? 'Action required' : 'All clear'}
        />
        <StatCard
          label="Alert Types"
          value={alerts.length > 0 ? [...new Set(alerts.map(a => a.alert_type))].join(', ') : 'None'}
        />
      </div>

      <SectionTitle>HMRC / Duty</SectionTitle>
      <div style={GRID(3)}>
        <StatCard
          label="Open W1 Period"
          value={openW1 ? `${MONTHS[(openW1.period_month ?? 1) - 1]} ${openW1.period_year}` : 'None'}
          accent={openW1 ? '#2563eb' : undefined}
        />
        <StatCard
          label="Locked (Pending Submit)"
          value={lockedW1 ? `${MONTHS[(lockedW1.period_month ?? 1) - 1]} ${lockedW1.period_year}` : 'None'}
          accent={lockedW1 ? '#d97706' : undefined}
        />
        <StatCard
          label="Latest Period"
          value={latestW1 ? `${MONTHS[(latestW1.period_month ?? 1) - 1]} ${latestW1.period_year}` : '—'}
          sub={latestW1 ? latestW1.status : undefined}
        />
      </div>

      <SectionTitle>SALSA Compliance</SectionTitle>
      <div style={GRID(4)}>
        <StatCard
          label="Open Non-Conformances"
          value={salsa?.open_non_conformances ?? '—'}
          accent={salsa?.open_non_conformances > 0 ? '#dc2626' : undefined}
        />
        <StatCard
          label="Critical NCs"
          value={salsa?.critical_non_conformances ?? '—'}
          accent={salsa?.critical_non_conformances > 0 ? '#dc2626' : undefined}
        />
        <StatCard
          label="Open Complaints"
          value={salsa?.open_complaints ?? '—'}
          accent={salsa?.open_complaints > 0 ? '#d97706' : undefined}
        />
        <StatCard
          label="Training Expiring (30d)"
          value={salsa?.training_expiring_30_days ?? '—'}
          accent={salsa?.training_expiring_30_days > 0 ? '#d97706' : undefined}
        />
        <StatCard
          label="Overdue Maintenance"
          value={salsa?.overdue_maintenance ?? '—'}
          accent={salsa?.overdue_maintenance > 0 ? '#dc2626' : undefined}
        />
        {salsa?.last_traceability_test && (
          <StatCard
            label="Last Traceability Test"
            value={salsa.last_traceability_test.passed ? '✓ Passed' : '✗ Failed'}
            sub={`${salsa.last_traceability_test.test_ref} · ${fmt(salsa.last_traceability_test.test_date)}`}
            accent={salsa.last_traceability_test.passed ? '#16a34a' : '#dc2626'}
          />
        )}
        {salsa?.next_food_safety_review && (
          <StatCard
            label="Next Food Safety Review"
            value={fmt(salsa.next_food_safety_review.next_review_due)}
            sub={salsa.next_food_safety_review.review_ref ?? undefined}
          />
        )}
        {salsa?.supplier_reviews_due_30_days != null && (
          <StatCard
            label="Supplier Reviews Due (30d)"
            value={salsa.supplier_reviews_due_30_days}
            accent={salsa.supplier_reviews_due_30_days > 0 ? '#d97706' : undefined}
          />
        )}
      </div>
    </div>
  );
}

// ── Production Tab ────────────────────────────────────────────────────────────

function ProductionTab({ v }) {
  const [status,  setStatus]  = useState('');
  const [context, setContext] = useState('');

  const qs = new URLSearchParams({ limit: 200 });
  if (status)  qs.set('status', status);
  if (context) qs.set('business_context', context);

  const { data: raw, loading } = useApi(`/production/runs?${qs}`, [v, status, context]);
  const runs = raw?.runs ?? [];

  const totalLitres = runs
    .filter(r => r.status === 'completed')
    .reduce((s, r) => s + (parseFloat(r.actual_volume_litres) || 0), 0);

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <SelectEl value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          <option value="planned">Planned</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </SelectEl>
        <SelectEl value={context} onChange={e => setContext(e.target.value)}>
          <option value="">All contexts</option>
          <option value="own_brand">Own Brand</option>
          <option value="client">Client</option>
        </SelectEl>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>
          {runs.length} run{runs.length !== 1 ? 's' : ''}
          {totalLitres > 0 && ` · ${totalLitres.toFixed(1)} L produced`}
        </span>
      </div>

      <div style={TABLE_WRAP}>
        <table style={TABLE}>
          <thead>
            <tr>
              {['Run Ref', 'Recipe', 'Spirit Type', 'Context', 'Status', 'Scheduled Start', 'Target (L)', 'Actual (L)'].map(h => (
                <th key={h} style={TH}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRow cols={8} />
            ) : runs.length === 0 ? (
              <EmptyRow cols={8} />
            ) : runs.map(r => (
              <tr key={r.id}>
                <td style={TD}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--pld-green)', fontWeight: 600 }}>
                    {r.run_ref ?? '—'}
                  </span>
                </td>
                <td style={TD}>{r.recipe_name ?? '—'}</td>
                <td style={{ ...TD, textTransform: 'capitalize' }}>{r.spirit_type ?? '—'}</td>
                <td style={TD}>
                  <Badge
                    text={r.business_context === 'own_brand' ? 'Own Brand' : r.business_context === 'client' ? 'Client' : r.business_context ?? '—'}
                    color={r.business_context === 'own_brand' ? '#7c3aed' : '#2563eb'}
                  />
                </td>
                <td style={TD}>
                  <Badge text={runStatusLabel(r.status)} color={runStatusColor(r.status)} />
                </td>
                <td style={TD}>{fmt(r.scheduled_start_date)}</td>
                <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {r.target_volume_litres ? Number(r.target_volume_litres).toFixed(1) : '—'}
                </td>
                <td style={{ ...TD, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {r.actual_volume_litres ? Number(r.actual_volume_litres).toFixed(1) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Stock Tab ─────────────────────────────────────────────────────────────────

function StockTab({ v }) {
  const [category, setCategory] = useState('');
  const [search,   setSearch]   = useState('');

  const qs = new URLSearchParams();
  if (category) qs.set('category', category);
  if (search)   qs.set('search', search);

  const { data: stockRaw, loading } = useApi(`/stock?${qs}`, [v, category, search]);
  const { data: alertsRaw }         = useApi('/stock/alerts/unresolved', [v]);

  const items      = stockRaw?.stock_items ?? [];
  const alerts     = alertsRaw?.alerts ?? [];
  const alertedIds = new Set(alerts.map(a => a.stock_item_id).filter(Boolean));
  const categories = [...new Set(items.map(i => i.category).filter(Boolean))].sort();

  return (
    <div>
      {alerts.length > 0 && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius)',
          padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#991b1b',
        }}>
          ⚠️ <strong>{alerts.length} unresolved stock alert{alerts.length !== 1 ? 's' : ''}</strong>
          {' — '}highlighted below. Resolve these in the Stock module.
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          placeholder="Search items…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            padding: '6px 10px', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', background: 'var(--surface)',
            color: 'var(--text)', fontSize: 13, width: 200,
          }}
        />
        <SelectEl value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </SelectEl>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>
          {items.length} item{items.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div style={TABLE_WRAP}>
        <table style={TABLE}>
          <thead>
            <tr>
              {['Name', 'Category', 'SKU', 'Bonded', 'Alcoholic', 'Unit', ''].map(h => (
                <th key={h} style={TH}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <LoadingRow cols={7} />
            ) : items.length === 0 ? (
              <EmptyRow cols={7} />
            ) : items.map(item => {
              const hasAlert = alertedIds.has(item.id);
              return (
                <tr key={item.id} style={{ background: hasAlert ? '#fef2f2' : undefined }}>
                  <td style={TD}>
                    <span style={{ fontWeight: hasAlert ? 600 : 400 }}>{item.name}</span>
                  </td>
                  <td style={{ ...TD, textTransform: 'capitalize' }}>
                    {item.category?.replace(/_/g, ' ') ?? '—'}
                  </td>
                  <td style={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{item.sku ?? '—'}</td>
                  <td style={TD}>
                    {item.is_bonded
                      ? <Badge text="Bonded" color="#7c3aed" />
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={TD}>
                    {item.is_alcoholic
                      ? <Badge text="ABV" color="#2563eb" />
                      : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={TD}>{item.unit_of_measure ?? '—'}</td>
                  <td style={TD}>
                    {hasAlert && <Badge text="⚠ Alert" color="#dc2626" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── HMRC Tab ──────────────────────────────────────────────────────────────────

function HmrcTab({ v }) {
  const [year, setYear] = useState('');

  const qs = new URLSearchParams();
  if (year) qs.set('year', year);

  const { data: periodsRaw, loading: perLoading } = useApi(`/hmrc/w1-periods?${qs}`, [v, year]);
  const { data: movRaw,     loading: movLoading }  = useApi('/hmrc/duty-movements', [v]);

  const periods   = Array.isArray(periodsRaw) ? periodsRaw : [];
  const movements = Array.isArray(movRaw)     ? movRaw     : [];

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <SectionTitle style={{ margin: 0 }}>W1 Return Periods</SectionTitle>
        <SelectEl value={year} onChange={e => setYear(e.target.value)} style={{ width: 120 }}>
          <option value="">All years</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </SelectEl>
      </div>

      <div style={TABLE_WRAP}>
        <table style={TABLE}>
          <thead>
            <tr>
              {['Period', 'Status', 'Submitted At', 'Acknowledged At', 'Acknowledgement Ref'].map(h => (
                <th key={h} style={TH}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {perLoading ? (
              <LoadingRow cols={5} />
            ) : periods.length === 0 ? (
              <EmptyRow cols={5} msg="No W1 periods found" />
            ) : periods.map(p => (
              <tr key={p.id}>
                <td style={TD}>
                  <span style={{ fontWeight: 600 }}>
                    {MONTHS[(p.period_month ?? 1) - 1]} {p.period_year}
                  </span>
                </td>
                <td style={TD}>
                  <Badge
                    text={p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    color={w1StatusColor(p.status)}
                  />
                </td>
                <td style={TD}>{fmt(p.submitted_at)}</td>
                <td style={TD}>{fmt(p.acknowledged_at)}</td>
                <td style={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>
                  {p.acknowledgement_ref ?? '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SectionTitle>Duty Movements</SectionTitle>
      <div style={TABLE_WRAP}>
        <table style={TABLE}>
          <thead>
            <tr>
              {['Ref', 'Type', 'Business Context', 'Payment Status', 'Created'].map(h => (
                <th key={h} style={TH}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {movLoading ? (
              <LoadingRow cols={5} />
            ) : movements.length === 0 ? (
              <EmptyRow cols={5} msg="No duty movements found" />
            ) : movements.slice(0, 50).map(m => (
              <tr key={m.id}>
                <td style={{ ...TD, fontFamily: 'monospace', fontSize: 12 }}>{m.movement_ref ?? '—'}</td>
                <td style={{ ...TD, textTransform: 'capitalize' }}>
                  {m.movement_type?.replace(/_/g, ' ') ?? '—'}
                </td>
                <td style={{ ...TD, textTransform: 'capitalize' }}>
                  {m.business_context?.replace(/_/g, ' ') ?? '—'}
                </td>
                <td style={TD}>
                  <Badge
                    text={m.payment_status ?? 'unknown'}
                    color={
                      m.payment_status === 'paid'    ? '#16a34a' :
                      m.payment_status === 'pending' ? '#d97706' :
                      m.payment_status === 'overdue' ? '#dc2626' : '#6b7280'
                    }
                  />
                </td>
                <td style={TD}>{fmt(m.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── SALSA Tab ─────────────────────────────────────────────────────────────────

function SalsaTab({ v }) {
  const { data: salsa, loading } = useApi('/salsa/dashboard', [v]);

  if (loading) {
    return <div style={{ color: 'var(--text-muted)', padding: 32, textAlign: 'center' }}>Loading compliance summary…</div>;
  }
  if (!salsa) {
    return <div style={{ color: 'var(--text-muted)', padding: 32, textAlign: 'center' }}>No data available.</div>;
  }

  const lastTest   = salsa.last_traceability_test;
  const nextReview = salsa.next_food_safety_review;
  const reviewOverdue = nextReview && new Date(nextReview.next_review_due) < new Date();
  const reviewSoon    = nextReview && !reviewOverdue && new Date(nextReview.next_review_due) < new Date(Date.now() + 30 * 86400000);

  return (
    <div>
      <SectionTitle>Non-Conformances &amp; Complaints</SectionTitle>
      <div style={GRID(4)}>
        <StatCard label="Open NCs"       value={salsa.open_non_conformances}    accent={salsa.open_non_conformances > 0    ? '#dc2626' : undefined} />
        <StatCard label="Critical NCs"   value={salsa.critical_non_conformances} accent={salsa.critical_non_conformances > 0 ? '#dc2626' : undefined} />
        <StatCard label="Open Complaints" value={salsa.open_complaints}          accent={salsa.open_complaints > 0          ? '#d97706' : undefined} />
        <StatCard label="Supplier Reviews Due (30d)" value={salsa.supplier_reviews_due_30_days} accent={salsa.supplier_reviews_due_30_days > 0 ? '#d97706' : undefined} />
      </div>

      <SectionTitle>Training &amp; People</SectionTitle>
      <div style={GRID(2)}>
        <StatCard
          label="Certificates Expiring in 30 Days"
          value={salsa.training_expiring_30_days}
          sub={salsa.training_expiring_30_days > 0 ? 'Review training records' : 'All current'}
          accent={salsa.training_expiring_30_days > 0 ? '#d97706' : undefined}
        />
        <StatCard
          label="Overdue Maintenance Items"
          value={salsa.overdue_maintenance}
          sub={salsa.overdue_maintenance > 0 ? 'Action required' : 'Schedule up to date'}
          accent={salsa.overdue_maintenance > 0 ? '#dc2626' : undefined}
        />
      </div>

      <SectionTitle>Traceability &amp; Food Safety</SectionTitle>
      <div style={GRID(3)}>
        {lastTest ? (
          <StatCard
            label="Last Traceability Test"
            value={lastTest.passed ? '✓ Passed' : '✗ Failed'}
            sub={`${lastTest.test_ref} · ${fmt(lastTest.test_date)}`}
            accent={lastTest.passed ? '#16a34a' : '#dc2626'}
          />
        ) : (
          <StatCard label="Last Traceability Test" value="None recorded" />
        )}
        {nextReview ? (
          <StatCard
            label="Next Food Safety Review"
            value={fmt(nextReview.next_review_due)}
            sub={nextReview.review_ref ?? undefined}
            accent={reviewOverdue ? '#dc2626' : reviewSoon ? '#d97706' : undefined}
          />
        ) : (
          <StatCard label="Next Food Safety Review" value="None scheduled" />
        )}
      </div>

      <div style={{
        marginTop: 8, padding: '10px 16px',
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text-muted)',
      }}>
        For detailed records and status workflows, use the <strong>SALSA</strong> module. This tab shows a live compliance snapshot only.
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Reports() {
  const [tab, setTab] = useState('overview');
  const [v, bump]     = useReducer(n => n + 1, 0);

  return (
    <div style={PAGE}>
      <div style={HEADER}>
        <h1 style={H1}>Reports</h1>
        <p style={SUB}>Live data snapshots across production, stock, HMRC duty, and SALSA compliance.</p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <TabBar tab={tab} setTab={setTab} />
          <button
            onClick={bump}
            title="Refresh all data"
            style={{
              marginBottom: 2, padding: '5px 12px', fontSize: 12, fontWeight: 600,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius)', cursor: 'pointer', color: 'var(--text)',
            }}
          >
            ↺ Refresh
          </button>
        </div>
      </div>

      <div style={BODY}>
        {tab === 'overview'   && <OverviewTab   v={v} />}
        {tab === 'production' && <ProductionTab v={v} />}
        {tab === 'stock'      && <StockTab      v={v} />}
        {tab === 'hmrc'       && <HmrcTab       v={v} />}
        {tab === 'salsa'      && <SalsaTab      v={v} />}
      </div>
    </div>
  );
}
